import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../supabaseAuth';

const AdminUserDetailModal = ({ show, onHide, user, onToggleAdmin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { getAccessToken } = useSupabase();

  // Fetch user's subscriptions and transactions
  const fetchUserDetails = async () => {
    if (!user || !show) return;

    setLoadingDetails(true);
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      // Fetch subscriptions
      // console.log(`Fetching subscriptions for user ${user.id}...`);
      const subsResponse = await fetch(`${apiBaseUrl}/api/admin/users/${user.id}/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // console.log(`Subscriptions response status: ${subsResponse.status}`);
      if (subsResponse.ok) {
        const subsData = await subsResponse.json();
        // console.log('Subscriptions data:', subsData);
        setUserSubscriptions(subsData.subscriptions || []);
      } else {
        console.error('Failed to fetch subscriptions:', await subsResponse.text());
      }

      // Fetch recent transactions
      // console.log(`Fetching transactions for user ${user.id}...`);
      const transResponse = await fetch(`${apiBaseUrl}/api/admin/users/${user.id}/transactions?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // console.log(`Transactions response status: ${transResponse.status}`);
      if (transResponse.ok) {
        const transData = await transResponse.json();
        // console.log('Transactions data:', transData);
        setUserTransactions(transData.transactions || []);
      } else {
        console.error('Failed to fetch transactions:', await transResponse.text());
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user, show]);

  if (!user) return null;

  const {
    id,
    email,
    first_name,
    last_name,
    phone,
    city,
    state,
    country,
    subscription_count,
    is_admin,
    created_at,
    supabase_id
  } = user;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Handle admin toggle
  const handleToggleAdmin = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await onToggleAdmin(user.id, user.is_admin);
      setSuccess(`User ${is_admin ? 'removed from' : 'granted'} admin privileges successfully.`);
      // Close modal after success
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update admin status.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { class: 'bg-success', icon: '✅' },
      'paused': { class: 'bg-warning', icon: '⏸️' },
      'cancelled': { class: 'bg-danger', icon: '❌' },
      'pending': { class: 'bg-secondary', icon: '⏳' }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    
    return (
      <span className={`badge ${config.class}`}>
        {config.icon} {status || 'Unknown'}
      </span>
    );
  };

  if (!show) return null;

  return (
    <div 
      className="modal fade show" 
      style={{ 
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1050
      }} 
      tabIndex="-1"
    >
      <div 
        className="modal-backdrop fade show" 
        onClick={onHide}
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: -1
        }}
      ></div>
      <div 
        className="modal-dialog modal-xl" 
        style={{
          position: 'relative',
          margin: '1.75rem auto',
          maxHeight: 'calc(100vh - 3.5rem)',
          display: 'flex',
          alignItems: 'flex-start'
        }}
      >
        <div 
          className="modal-content" 
          style={{ 
            maxHeight: '100%', 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
          }}
        >
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-person me-2"></i>
              User Details - {first_name || last_name ? `${first_name} ${last_name}` : email}
              {is_admin && (
                <span className="badge bg-warning ms-2">
                  <i className="bi bi-shield-check me-1"></i>
                  Admin
                </span>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>
          
          <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            
            {success && (
              <div className="alert alert-success">
                <i className="bi bi-check-circle me-2"></i>
                {success}
              </div>
            )}

            <div className="row">
              {/* User Information */}
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-person-badge me-2"></i>
                      Personal Information
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <strong>Email:</strong>
                      <div>{email}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Name:</strong>
                      <div>{first_name || last_name ? `${first_name || ''} ${last_name || ''}`.trim() : 'Not provided'}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Phone:</strong>
                      <div>{phone || 'Not provided'}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Location:</strong>
                      <div>
                        {city && state ? `${city}, ${state}` : 'Not provided'}
                        {country && <><br /><small className="text-muted">{country}</small></>}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong>User ID:</strong>
                      <div>
                        <code>#{id}</code>
                        {supabase_id && (
                          <>
                            <br />
                            <small className="text-muted">Supabase: {supabase_id}</small>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong>Registered:</strong>
                      <div>{formatDate(created_at)}</div>
                    </div>
                    <div className="mb-0">
                      <strong>Account Type:</strong>
                      <div>
                        {is_admin ? (
                          <span className="badge bg-warning">
                            <i className="bi bi-shield-check me-1"></i>
                            Administrator
                          </span>
                        ) : (
                          <span className="badge bg-light text-dark">Regular User</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscriptions */}
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-credit-card me-2"></i>
                      Subscriptions ({subscription_count || 0})
                    </h6>
                  </div>
                  <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {loadingDetails ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm me-2"></div>
                        Loading subscriptions...
                      </div>
                    ) : userSubscriptions.length === 0 ? (
                      <div className="text-center py-3 text-muted">
                        <i className="bi bi-inbox display-6"></i>
                        <p className="mb-0">No subscriptions found</p>
                      </div>
                    ) : (
                      userSubscriptions.map(subscription => (
                        <div key={subscription.id} className="border rounded p-2 mb-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>#{subscription.id}</strong> - {subscription.metal_type?.toUpperCase()}
                              <br />
                              <small className="text-muted">
                                {formatCurrency(subscription.amount_per_period)} per {subscription.period}
                              </small>
                            </div>
                            <div className="text-end">
                              {getStatusBadge(subscription.status)}
                              <br />
                              <small className="text-muted">
                                {formatCurrency(subscription.current_balance)}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="row mt-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-receipt me-2"></i>
                      Recent Transactions
                    </h6>
                  </div>
                  <div className="card-body">
                    {loadingDetails ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm me-2"></div>
                        Loading transactions...
                      </div>
                    ) : userTransactions.length === 0 ? (
                      <div className="text-center py-3 text-muted">
                        <i className="bi bi-receipt display-6"></i>
                        <p className="mb-0">No transactions found</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Type</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userTransactions.map(transaction => (
                              <tr key={transaction.id}>
                                <td>#{transaction.id}</td>
                                <td>
                                  <span className="badge bg-secondary">
                                    {transaction.transaction_type}
                                  </span>
                                </td>
                                <td>{formatCurrency(transaction.amount)}</td>
                                <td>
                                  <span className={`badge ${
                                    transaction.status === 'completed' ? 'bg-success' :
                                    transaction.status === 'pending' ? 'bg-warning' :
                                    'bg-danger'
                                  }`}>
                                    {transaction.status}
                                  </span>
                                </td>
                                <td>
                                  <small>{formatDate(transaction.created_at)}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <div className="d-flex justify-content-between w-100">
              <div>
                <button 
                  className={`btn ${is_admin ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => {
                    const action = is_admin ? 'remove admin privileges from' : 'grant admin privileges to';
                    if (window.confirm(`Are you sure you want to ${action} ${email}?`)) {
                      handleToggleAdmin();
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className={`bi ${is_admin ? 'bi-shield-x' : 'bi-shield-check'} me-2`}></i>
                      {is_admin ? 'Remove Admin' : 'Make Admin'}
                    </>
                  )}
                </button>
              </div>
              <button className="btn btn-secondary" onClick={onHide}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetailModal;